import type { AdminHouseStreetMatchResult, AdminNeighborhoodReview, HouseInclusionStatus, ReviewHouse, ReviewStreetUnit } from '../domain/types';
import { addReadableStreetBasemap } from '../map/carto-basemap';
import { makeMapPageFriendly } from '../map/page-friendly-map';
import { mapZoom, mapZoomBand, zoomInterpolate } from '../map/zoom-presentation';

declare const L: any;

const INCLUDED_COLOR = '#198754';
const EXCLUDED_COLOR = '#c43d3d';
const CHANGE_COLOR = '#d6a619';
const SELECTED_COLOR = '#1769c2';
const SAVING_COLOR = '#8d99a5';
const STREET_COLOR = '#6f7f8f';
const FINE_POINTER_TAP_TOLERANCE_PIXELS = 30;
const COARSE_POINTER_TAP_TOLERANCE_PIXELS = 48;
const DRAG_THRESHOLD_PIXELS = 7;
const LASSO_POINT_SPACING_PIXELS = 3;
const LASSO_CLOSE_TOLERANCE_PIXELS = 28;
const LASSO_MIN_LENGTH_PIXELS = 70;
const LASSO_MIN_AREA_PIXELS = 250;
const BRUSH_SELECTION_RADIUS_PIXELS = 12;

type ReviewMapMode = 'pan' | 'select';
type ReviewMapFilter = 'CHANGES' | 'UNMATCHED' | 'ALL';
type ScreenPoint = { x: number; y: number };

type SelectionGesture = {
  pointerId: number;
  points: ScreenPoint[];
  additive: boolean;
  pathLength: number;
};

type SecondaryPanGesture = {
  pointerId: number;
  lastPoint: ScreenPoint;
};

export interface AdminReviewMapHandle {
  updateHouse(house: ReviewHouse): void;
  updateHouses(houses: ReviewHouse[]): void;
  clearSelection(): void;
  setChangesOnly(enabled: boolean): void;
  setFilter(filter: ReviewMapFilter): void;
  destroy(): void;
}

export interface AdminReviewMapCallbacks {
  onBulkChange(
    houses: ReviewHouse[],
    nextStatus: HouseInclusionStatus
  ): Promise<ReviewHouse[]>;
  onMatchStreet(
    houses: ReviewHouse[],
    streetUnit: ReviewStreetUnit,
    method: 'ADMIN_SUGGESTED' | 'ADMIN_SELECTED'
  ): Promise<AdminHouseStreetMatchResult[]>;
  onCreateManualStreet(house: ReviewHouse): Promise<AdminHouseStreetMatchResult>;
  onStreetMatchesApplied(results: AdminHouseStreetMatchResult[]): void;
}

export function mountAdminReviewMap(
  review: AdminNeighborhoodReview,
  callbacks: AdminReviewMapCallbacks
): AdminReviewMapHandle {
  const element = document.querySelector<HTMLDivElement>('#admin-review-map');
  if (!element || typeof L === 'undefined') return emptyHandle();

  const renderer = L.canvas({ padding: 0.5 });
  const map = L.map(element, {
    renderer,
    preferCanvas: true,
    zoomControl: true,
    scrollWheelZoom: false
  });
  const pageFriendlyMap = makeMapPageFriendly(map, element);
  addReadableStreetBasemap(map, L);

  const bounds = L.latLngBounds([]);
  const streetUnitById = new Map<string, ReviewStreetUnit>();
  const streetLayerById = new Map<string, any>();
  const streetPickLayerById = new Map<string, any>();
  let streetPickMode = false;
  let suggestedStreetUnitId: string | null = null;

  const addStreetUnit = (unit: ReviewStreetUnit): void => {
    streetUnitById.set(unit.id, unit);
    const latLngs: Array<[number, number]> = unit.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    if (latLngs.length < 2) return;
    const streetLayer = L.polyline(latLngs, reviewStreetStyle(mapZoom(map), renderer))
      .bindTooltip(unit.manual ? `${unit.displayStreet} · manual segment` : unit.displayStreet, { sticky: true })
      .addTo(map);
    streetLayerById.set(unit.id, streetLayer);
    const pickLayer = L.polyline(latLngs, {
      renderer,
      color: '#1769c2',
      weight: 20,
      opacity: 0.001,
      interactive: true
    }).bindTooltip(`Use ${unit.displayStreet}`, { sticky: true, className: 'street-pick-tooltip' });
    pickLayer.on('click', (event: any) => {
      if (!streetPickMode || busy) return;
      L.DomEvent.stop(event);
      void applyStreetMatch(unit, 'ADMIN_SELECTED');
    });
    streetPickLayerById.set(unit.id, pickLayer);
    latLngs.forEach((point) => bounds.extend(point));
  };

  for (const unit of review.streetUnits) addStreetUnit(unit);

  const markerById = new Map<string, any>();
  const houseById = new Map(review.houses.map((house) => [house.id, house]));
  const selectedIds = new Set<string>();
  let mode: ReviewMapMode = 'pan';
  let busy = false;
  let changesOnly = (review.neighborhood.pendingReviewChanges ?? 0) > 0;
  let unmatchedOnly = false;
  let selectionGesture: SelectionGesture | null = null;
  let secondaryPanGesture: SecondaryPanGesture | null = null;
  let selectionOverlay: SVGSVGElement | null = null;
  let selectionPath: SVGPathElement | null = null;
  let selectionStartDot: SVGCircleElement | null = null;

  for (const house of review.houses) {
    if (!Number.isFinite(house.lat) || !Number.isFinite(house.lng)) continue;
    const marker = L.circleMarker(
      [house.lat, house.lng],
      { ...markerOptions(house, renderer, false, 15), interactive: false }
    );
    marker.bindPopup(popupHtml(house));
    if (!changesOnly || house.reviewRequired) marker.addTo(map);
    markerById.set(house.id, marker);
    bounds.extend([house.lat, house.lng]);
  }

  if (bounds.isValid()) map.fitBounds(bounds.pad(0.04), { maxZoom: 17 });
  const reviewSummaryMarker = bounds.isValid()
    ? L.marker(bounds.getCenter(), {
        keyboard: true,
        riseOnHover: true,
        icon: L.divIcon({
          className: 'coverage-neighborhood-summary-icon',
          html: `<div class="coverage-neighborhood-summary coverage-neighborhood-summary--review"><strong>${escapeHtml(review.neighborhood.name)}</strong><span>${review.houses.length.toLocaleString()} house dots · click to zoom in</span></div>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0]
        })
      })
    : null;
  reviewSummaryMarker?.on('click', () => {
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.04), { maxZoom: 17 });
  });
  window.requestAnimationFrame(() => map.invalidateSize());

  const panButton = document.querySelector<HTMLButtonElement>('#review-mode-pan');
  const selectButton = document.querySelector<HTMLButtonElement>('#review-mode-select');
  const excludeButton = document.querySelector<HTMLButtonElement>('#exclude-selected');
  const restoreButton = document.querySelector<HTMLButtonElement>('#restore-selected');
  const clearButton = document.querySelector<HTMLButtonElement>('#clear-selection');
  const selectionSummary = document.querySelector<HTMLSpanElement>('#review-selection-summary');
  const selectionBar = document.querySelector<HTMLDivElement>('#review-selection-bar');
  const changesOnlyInput = document.querySelector<HTMLInputElement>('#review-changes-only');
  const unmatchedOnlyInput = document.querySelector<HTMLInputElement>('#review-unmatched-only');
  const resolutionPanel = document.querySelector<HTMLDivElement>('#unmatched-resolution-panel');
  const resolutionAddress = document.querySelector<HTMLElement>('#unmatched-resolution-address');
  const resolutionSuggestion = document.querySelector<HTMLElement>('#unmatched-resolution-suggestion');
  const useSuggestedButton = document.querySelector<HTMLButtonElement>('#use-suggested-street');
  const chooseStreetButton = document.querySelector<HTMLButtonElement>('#choose-street-segment');
  const createManualStreetButton = document.querySelector<HTMLButtonElement>('#create-manual-street');
  const cancelStreetPickButton = document.querySelector<HTMLButtonElement>('#cancel-street-pick');
  const modeTools = document.querySelector<HTMLDivElement>('.review-map-tools');
  const mapTip = document.querySelector<HTMLDivElement>('#review-map-tip');
  const changesFocusButton = document.querySelector<HTMLButtonElement>('#review-focus-changes');
  const unmatchedFocusButton = document.querySelector<HTMLButtonElement>('#review-focus-unmatched');
  const allFocusButton = document.querySelector<HTMLButtonElement>('#review-focus-all');

  let intentionalTouchPan = false;

  const setMode = (nextMode: ReviewMapMode, intentional = false): void => {
    cancelPointerGestures();
    mode = nextMode;
    const selecting = mode === 'select';
    if (selecting) intentionalTouchPan = false;
    else if (intentional) intentionalTouchPan = true;

    element.classList.toggle('review-select-mode', selecting);
    element.dataset.reviewMode = mode;
    modeTools?.setAttribute('data-review-mode', mode);
    pageFriendlyMap.setIntentionalTouchDrag(!selecting && intentionalTouchPan);

    const panningIsActive = !selecting && (!pageFriendlyMap.coarsePointer || intentionalTouchPan);
    panButton?.classList.toggle('active', panningIsActive);
    selectButton?.classList.toggle('active', selecting);
    panButton?.setAttribute('aria-pressed', String(panningIsActive));
    selectButton?.setAttribute('aria-pressed', String(selecting));
    if (mapTip) {
      mapTip.innerHTML = selecting
        ? '<strong>Select dots is active.</strong> Tap anywhere on or near a house lot, swipe across several houses, or left-drag a loop. Hold Shift or Command to add to the current selection.'
        : pageFriendlyMap.coarsePointer && !intentionalTouchPan
          ? '<strong>Page scrolling is active.</strong> Swipe with one finger to move down the page, use two fingers to zoom the map, or tap <strong>Move map</strong> to drag it.'
          : '<strong>Move map is active.</strong> Drag normally to move the map. Click <strong>Select dots</strong> when you are ready to choose houses.';
    }
  };

  const handleModeControl = (event: Event): void => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('#review-mode-pan, #review-mode-select')
      : null;
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    setMode(target.id === 'review-mode-select' ? 'select' : 'pan', true);
    target.blur();
  };

  const selectedUnmatchedHouses = (): ReviewHouse[] => {
    if (!selectedIds.size) return [];
    const houses = [...selectedIds]
      .map((houseId) => houseById.get(houseId))
      .filter((house): house is ReviewHouse => Boolean(house));
    return houses.length === selectedIds.size && houses.every(isUnmatchedIncluded) ? houses : [];
  };

  const suggestedStreetForHouse = (house: ReviewHouse): { unit: ReviewStreetUnit; distanceMeters: number } | null => {
    const normalizedStreet = normalizeStreetName(house.street);
    const core = streetCore(normalizedStreet);
    const ranked = [...streetUnitById.values()]
      .map((unit) => ({
        unit,
        distanceMeters: projectDistanceMeters(house, unit.geometry.coordinates),
        exactStreet: normalizeStreetName(unit.street) === normalizedStreet || streetCore(unit.street) === core
      }))
      .filter((candidate) => Number.isFinite(candidate.distanceMeters))
      .sort((left, right) => Number(right.exactStreet) - Number(left.exactStreet) || left.distanceMeters - right.distanceMeters);
    const best = ranked[0];
    return best ? { unit: best.unit, distanceMeters: best.distanceMeters } : null;
  };

  const refreshStreetStyles = (): void => {
    const zoom = mapZoom(map);
    for (const [unitId, layer] of streetLayerById) {
      const suggested = unitId === suggestedStreetUnitId;
      layer.setStyle(suggested
        ? { ...reviewStreetStyle(zoom, renderer), color: '#1769c2', weight: zoomInterpolate(zoom, 2, 6), opacity: 0.9 }
        : reviewStreetStyle(zoom, renderer));
    }
  };

  const setStreetPickMode = (enabled: boolean): void => {
    streetPickMode = enabled;
    element.classList.toggle('review-street-pick-mode', enabled);
    for (const layer of streetPickLayerById.values()) {
      if (enabled && !map.hasLayer(layer)) layer.addTo(map);
      if (!enabled && map.hasLayer(layer)) map.removeLayer(layer);
    }
    chooseStreetButton?.classList.toggle('active', enabled);
    if (cancelStreetPickButton) cancelStreetPickButton.hidden = !enabled;
    if (mapTip) {
      if (enabled) {
        const count = selectedUnmatchedHouses().length;
        mapTip.innerHTML = `<strong>Choose street segment is active.</strong> Click the mapped street line for the ${count === 1 ? 'selected house' : `${count.toLocaleString()} selected houses`}. Use Cancel if the correct street is missing.`;
      } else if (mode === 'select') {
        mapTip.innerHTML = '<strong>Select dots is active.</strong> Select one house or swipe across several unmatched houses, then connect them to a street.';
      }
    }
  };

  const applyStreetMatchResults = (results: AdminHouseStreetMatchResult[]): void => {
    if (!results.length) return;
    for (const result of results) {
      houseById.set(result.house.id, result.house);
      const marker = markerById.get(result.house.id);
      marker?.setPopupContent(popupHtml(result.house));
      if (result.createdStreetUnit && !streetUnitById.has(result.streetUnit.id)) addStreetUnit(result.streetUnit);
      selectedIds.delete(result.house.id);
    }
    suggestedStreetUnitId = null;
    setStreetPickMode(false);
    callbacks.onStreetMatchesApplied(results);
    refreshStreetStyles();
    renderSelection();
  };

  const applyStreetMatch = async (
    unit: ReviewStreetUnit,
    method: 'ADMIN_SUGGESTED' | 'ADMIN_SELECTED'
  ): Promise<void> => {
    const houses = selectedUnmatchedHouses();
    if (!houses.length || busy) return;
    if (houses.length > 1) {
      const confirmed = window.confirm(
        `Connect ${houses.length.toLocaleString()} selected houses to ${unit.displayStreet}?`
      );
      if (!confirmed) return;
    }
    busy = true;
    renderSelection();
    try {
      const results = await callbacks.onMatchStreet(houses, unit, method);
      applyStreetMatchResults(results);
    } finally {
      busy = false;
      renderSelection();
    }
  };

  const createManualStreet = async (): Promise<void> => {
    const houses = selectedUnmatchedHouses();
    const house = houses.length === 1 ? houses[0]! : null;
    if (!house || busy) return;
    const confirmed = window.confirm(
      `Create a manual street segment for ${house.address || 'this house'}? Use this only when the correct street is missing from the city map.`
    );
    if (!confirmed) return;
    busy = true;
    renderSelection();
    try {
      const result = await callbacks.onCreateManualStreet(house);
      applyStreetMatchResults([result]);
    } finally {
      busy = false;
      renderSelection();
    }
  };

  const updateResolutionPanel = (): void => {
    const houses = selectedUnmatchedHouses();
    const visible = houses.length > 0;
    resolutionPanel?.classList.toggle('hidden', !visible);
    if (!houses.length) {
      suggestedStreetUnitId = null;
      if (streetPickMode) setStreetPickMode(false);
      refreshStreetStyles();
      return;
    }

    const suggestions = houses.map((house) => suggestedStreetForHouse(house));
    const firstSuggestion = suggestions[0];
    const sharedSuggestion = firstSuggestion && suggestions.every((suggestion) => suggestion?.unit.id === firstSuggestion.unit.id)
      ? firstSuggestion
      : null;
    suggestedStreetUnitId = sharedSuggestion?.unit.id ?? null;

    if (resolutionAddress) {
      resolutionAddress.textContent = houses.length === 1
        ? houses[0]!.address || houses[0]!.street || 'Selected house'
        : `${houses.length.toLocaleString()} unmatched houses selected`;
    }
    const resolutionDetail = document.querySelector<HTMLElement>('#unmatched-resolution-detail');
    if (resolutionDetail) {
      resolutionDetail.textContent = houses.length === 1
        ? 'This house is real, but it still needs a street connection before sections can be built.'
        : 'Choose one mapped street segment to connect every selected house at once.';
    }
    if (resolutionSuggestion) {
      resolutionSuggestion.textContent = sharedSuggestion
        ? houses.length === 1
          ? `${sharedSuggestion.unit.displayStreet} · about ${formatDistance(sharedSuggestion.distanceMeters)} away`
          : `${sharedSuggestion.unit.displayStreet} · shared suggestion for all ${houses.length.toLocaleString()} houses`
        : houses.length > 1
          ? 'The selected houses have different suggestions. Choose a street segment on the map.'
          : 'No mapped street suggestion is available.';
    }
    if (useSuggestedButton) {
      useSuggestedButton.disabled = busy || !sharedSuggestion;
      useSuggestedButton.dataset.streetUnitId = sharedSuggestion?.unit.id ?? '';
      useSuggestedButton.textContent = houses.length > 1 ? 'Use shared suggested street' : 'Use suggested street';
    }
    if (chooseStreetButton) {
      chooseStreetButton.disabled = busy;
      chooseStreetButton.textContent = houses.length > 1 ? `Choose street for ${houses.length.toLocaleString()} houses` : 'Choose street segment';
    }
    if (createManualStreetButton) {
      createManualStreetButton.disabled = busy || houses.length !== 1;
      createManualStreetButton.title = houses.length !== 1
        ? 'Street data missing can only be handled one house at a time.'
        : '';
    }
    refreshStreetStyles();
  };

  const housePassesFilter = (house: ReviewHouse): boolean => {
    if (unmatchedOnly) return isUnmatchedIncluded(house);
    if (changesOnly) return house.reviewRequired === true;
    return true;
  };

  const renderSelection = (): void => {
    const zoom = mapZoom(map);
    const band = mapZoomBand(zoom);
    for (const [houseId, marker] of markerById) {
      const house = houseById.get(houseId);
      if (!house) continue;
      const selected = selectedIds.has(houseId);
      marker.setStyle(markerOptions(house, renderer, selected, zoom));
      const passesFilter = housePassesFilter(house);
      const shouldShow = passesFilter && (band !== 'FAR' || selected);
      if (shouldShow && !map.hasLayer(marker)) marker.addTo(map);
      if (!shouldShow && map.hasLayer(marker)) map.removeLayer(marker);
    }
    const selectedHouses = [...selectedIds]
      .map((houseId) => houseById.get(houseId))
      .filter((house): house is ReviewHouse => Boolean(house));
    const included = selectedHouses.filter((house) => house.inclusionStatus === 'INCLUDED').length;
    const excluded = selectedHouses.length - included;
    if (selectionSummary) {
      selectionSummary.textContent = selectedHouses.length
        ? `${selectedHouses.length.toLocaleString()} selected · ${included.toLocaleString()} green · ${excluded.toLocaleString()} red`
        : '0 selected';
    }
    if (selectionBar) selectionBar.classList.toggle('has-selection', selectedHouses.length > 0);
    const disabled = busy || selectedHouses.length === 0;
    if (excludeButton) excludeButton.disabled = disabled;
    if (restoreButton) restoreButton.disabled = disabled;
    if (clearButton) clearButton.disabled = busy || selectedHouses.length === 0;
    updateResolutionPanel();
  };

  const refreshZoomPresentation = (): void => {
    const zoom = mapZoom(map);
    const band = mapZoomBand(zoom);
    refreshStreetStyles();
    renderSelection();
    if (reviewSummaryMarker) {
      if (band === 'FAR' && !map.hasLayer(reviewSummaryMarker)) reviewSummaryMarker.addTo(map);
      if (band !== 'FAR' && map.hasLayer(reviewSummaryMarker)) map.removeLayer(reviewSummaryMarker);
    }
  };

  const fitFilteredHouses = (): void => {
    const filteredBounds = L.latLngBounds([]);
    for (const house of houseById.values()) {
      if (housePassesFilter(house)) filteredBounds.extend([house.lat, house.lng]);
    }
    if (filteredBounds.isValid()) map.fitBounds(filteredBounds.pad(0.12), { maxZoom: 18 });
  };

  const currentFilter = (): ReviewMapFilter => unmatchedOnly ? 'UNMATCHED' : changesOnly ? 'CHANGES' : 'ALL';

  const updateFilterControls = (): void => {
    const filter = currentFilter();
    changesFocusButton?.classList.toggle('active', filter === 'CHANGES');
    unmatchedFocusButton?.classList.toggle('active', filter === 'UNMATCHED');
    allFocusButton?.classList.toggle('active', filter === 'ALL');
    changesFocusButton?.setAttribute('aria-pressed', String(filter === 'CHANGES'));
    unmatchedFocusButton?.setAttribute('aria-pressed', String(filter === 'UNMATCHED'));
    allFocusButton?.setAttribute('aria-pressed', String(filter === 'ALL'));
  };

  const setFilter = (filter: ReviewMapFilter): void => {
    changesOnly = filter === 'CHANGES';
    unmatchedOnly = filter === 'UNMATCHED';
    if (changesOnlyInput) changesOnlyInput.checked = changesOnly;
    if (unmatchedOnlyInput) unmatchedOnlyInput.checked = unmatchedOnly;
    if (filter !== 'ALL') setMode('select', true);
    clearSelection();
    fitFilteredHouses();
    updateFilterControls();
  };

  const clearSelection = (): void => {
    selectedIds.clear();
    renderSelection();
  };

  const toggleNearestHouse = (
    point: ScreenPoint,
    additive: boolean,
    pointerType?: string
  ): void => {
    if (mapZoomBand(mapZoom(map)) === 'FAR') return;
    const visibleHouses = new Map([...houseById].filter(([, house]) => housePassesFilter(house)));
    const nearest = nearestHouseId(point, map, visibleHouses, tapTolerancePixels(pointerType, element));
    if (!nearest) {
      if (!additive) clearSelection();
      return;
    }
    if (!additive) {
      const wasOnlySelection = selectedIds.size === 1 && selectedIds.has(nearest);
      selectedIds.clear();
      if (!wasOnlySelection) selectedIds.add(nearest);
    } else if (selectedIds.has(nearest)) {
      selectedIds.delete(nearest);
    } else {
      selectedIds.add(nearest);
    }
    renderSelection();
  };

  const selectFreeform = (points: ScreenPoint[], additive: boolean): void => {
    if (!additive) selectedIds.clear();
    const closedLasso = isClosedLasso(points);

    for (const [houseId, house] of houseById) {
      if (!housePassesFilter(house)) continue;
      const point = map.latLngToContainerPoint([house.lat, house.lng]);
      const selected = closedLasso
        ? pointInPolygon(point, points) || distanceToPolyline(point, points, true) <= 4
        : distanceToPolyline(point, points, false) <= BRUSH_SELECTION_RADIUS_PIXELS;
      if (selected) selectedIds.add(houseId);
    }
    renderSelection();
  };

  const runBulkChange = async (nextStatus: HouseInclusionStatus): Promise<void> => {
    if (busy || !selectedIds.size) return;
    const selectedHouses = [...selectedIds]
      .map((houseId) => houseById.get(houseId))
      .filter((house): house is ReviewHouse => Boolean(house));
    if (!selectedHouses.length) return;

    busy = true;
    selectionBar?.classList.add('saving');
    for (const house of selectedHouses) {
      markerById.get(house.id)?.setStyle({ fillColor: SAVING_COLOR });
    }
    renderSelection();

    try {
      const updated = await callbacks.onBulkChange(selectedHouses, nextStatus);
      for (const house of updated) {
        houseById.set(house.id, house);
      }
      selectedIds.clear();
    } finally {
      busy = false;
      selectionBar?.classList.remove('saving');
      renderSelection();
    }
  };

  const pointFromEvent = (event: PointerEvent): ScreenPoint => {
    const rect = element.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const beginPointerGesture = (event: PointerEvent): void => {
    if (isLeafletControl(event.target) || streetPickMode) return;

    if (isSecondaryPointer(event)) {
      event.preventDefault();
      event.stopPropagation();
      cancelSelectionGesture();
      secondaryPanGesture = {
        pointerId: event.pointerId,
        lastPoint: pointFromEvent(event)
      };
      element.classList.add('review-secondary-pan');
      element.setPointerCapture?.(event.pointerId);
      return;
    }

    if (mode !== 'select' || busy || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const point = pointFromEvent(event);
    selectionGesture = {
      pointerId: event.pointerId,
      points: [point],
      additive: event.shiftKey || event.metaKey,
      pathLength: 0
    };
    createSelectionOverlay(point);
    element.setPointerCapture?.(event.pointerId);
  };

  const movePointerGesture = (event: PointerEvent): void => {
    if (secondaryPanGesture?.pointerId === event.pointerId) {
      event.preventDefault();
      event.stopPropagation();
      const point = pointFromEvent(event);
      const offsetX = secondaryPanGesture.lastPoint.x - point.x;
      const offsetY = secondaryPanGesture.lastPoint.y - point.y;
      if (offsetX || offsetY) map.panBy([offsetX, offsetY], { animate: false });
      secondaryPanGesture.lastPoint = point;
      return;
    }

    if (selectionGesture?.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    const point = pointFromEvent(event);
    const previous = selectionGesture.points[selectionGesture.points.length - 1]!;
    const distance = Math.hypot(point.x - previous.x, point.y - previous.y);
    if (distance < LASSO_POINT_SPACING_PIXELS) return;
    selectionGesture.pathLength += distance;
    selectionGesture.points.push(point);
    updateSelectionOverlay(selectionGesture.points);
  };

  const finishPointerGesture = (event: PointerEvent): void => {
    if (secondaryPanGesture?.pointerId === event.pointerId) {
      event.preventDefault();
      event.stopPropagation();
      secondaryPanGesture = null;
      element.classList.remove('review-secondary-pan');
      element.releasePointerCapture?.(event.pointerId);
      return;
    }

    if (selectionGesture?.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    const gesture = selectionGesture;
    const end = pointFromEvent(event);
    const last = gesture.points[gesture.points.length - 1]!;
    const finalDistance = Math.hypot(end.x - last.x, end.y - last.y);
    if (finalDistance >= LASSO_POINT_SPACING_PIXELS) {
      gesture.points.push(end);
      gesture.pathLength += finalDistance;
    }
    selectionGesture = null;
    removeSelectionOverlay();
    element.releasePointerCapture?.(event.pointerId);

    if (gesture.pathLength < DRAG_THRESHOLD_PIXELS) {
      toggleNearestHouse(end, gesture.additive, event.pointerType);
    } else {
      selectFreeform(gesture.points, gesture.additive);
    }
  };

  const cancelSelectionGesture = (): void => {
    selectionGesture = null;
    removeSelectionOverlay();
  };

  const cancelPointerGestures = (): void => {
    cancelSelectionGesture();
    secondaryPanGesture = null;
    element.classList.remove('review-secondary-pan');
  };

  const createSelectionOverlay = (start: ScreenPoint): void => {
    removeSelectionOverlay();
    const namespace = 'http://www.w3.org/2000/svg';
    selectionOverlay = document.createElementNS(namespace, 'svg');
    selectionOverlay.classList.add('review-selection-lasso');
    selectionOverlay.setAttribute('viewBox', `0 0 ${Math.max(1, element.clientWidth)} ${Math.max(1, element.clientHeight)}`);
    selectionOverlay.setAttribute('preserveAspectRatio', 'none');

    selectionPath = document.createElementNS(namespace, 'path');
    selectionPath.classList.add('review-selection-lasso-path');
    selectionPath.setAttribute('d', `M ${start.x} ${start.y}`);

    selectionStartDot = document.createElementNS(namespace, 'circle');
    selectionStartDot.classList.add('review-selection-lasso-start');
    selectionStartDot.setAttribute('cx', String(start.x));
    selectionStartDot.setAttribute('cy', String(start.y));
    selectionStartDot.setAttribute('r', '5');

    selectionOverlay.append(selectionPath, selectionStartDot);
    element.appendChild(selectionOverlay);
  };

  const updateSelectionOverlay = (points: ScreenPoint[]): void => {
    if (!selectionPath || !selectionStartDot) return;
    const closed = isClosedLasso(points);
    selectionPath.setAttribute('d', smoothSvgPath(points, closed));
    selectionPath.classList.toggle('is-closed', closed);
    selectionStartDot.classList.toggle('is-ready', closed);
  };

  const removeSelectionOverlay = (): void => {
    selectionOverlay?.remove();
    selectionOverlay = null;
    selectionPath = null;
    selectionStartDot = null;
  };

  const suppressContextMenu = (event: MouseEvent): void => {
    if (isLeafletControl(event.target)) return;
    event.preventDefault();
  };

  const showNearestPopup = (event: any): void => {
    if (streetPickMode || mode !== 'pan' || mapZoomBand(mapZoom(map)) === 'FAR') return;
    const visibleHouses = new Map([...houseById].filter(([, house]) => housePassesFilter(house)));
    const nearest = nearestHouseId(event.containerPoint, map, visibleHouses, tapTolerancePixels(undefined, element));
    if (nearest) markerById.get(nearest)?.openPopup();
  };

  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (streetPickMode) setStreetPickMode(false);
    else if (selectionGesture || secondaryPanGesture) cancelPointerGestures();
    else clearSelection();
  };

  modeTools?.addEventListener('click', handleModeControl);
  modeTools?.addEventListener('pointerup', handleModeControl);
  excludeButton?.addEventListener('click', () => { void runBulkChange('EXCLUDED'); });
  restoreButton?.addEventListener('click', () => { void runBulkChange('INCLUDED'); });
  clearButton?.addEventListener('click', clearSelection);
  changesOnlyInput?.addEventListener('change', () => setFilter(changesOnlyInput.checked ? 'CHANGES' : 'ALL'));
  unmatchedOnlyInput?.addEventListener('change', () => setFilter(unmatchedOnlyInput.checked ? 'UNMATCHED' : 'ALL'));
  changesFocusButton?.addEventListener('click', () => setFilter('CHANGES'));
  unmatchedFocusButton?.addEventListener('click', () => setFilter('UNMATCHED'));
  allFocusButton?.addEventListener('click', () => setFilter('ALL'));
  useSuggestedButton?.addEventListener('click', () => {
    const unit = streetUnitById.get(useSuggestedButton.dataset.streetUnitId ?? '');
    if (unit) void applyStreetMatch(unit, 'ADMIN_SUGGESTED');
  });
  chooseStreetButton?.addEventListener('click', () => setStreetPickMode(!streetPickMode));
  cancelStreetPickButton?.addEventListener('click', () => setStreetPickMode(false));
  createManualStreetButton?.addEventListener('click', () => { void createManualStreet(); });
  element.addEventListener('pointerdown', beginPointerGesture, true);
  element.addEventListener('pointermove', movePointerGesture, true);
  element.addEventListener('pointerup', finishPointerGesture, true);
  element.addEventListener('pointercancel', cancelPointerGestures, true);
  element.addEventListener('contextmenu', suppressContextMenu);
  map.on('click', showNearestPopup);
  map.on('zoomend', refreshZoomPresentation);
  window.addEventListener('keydown', handleKeydown);
  setMode('pan');
  updateFilterControls();
  refreshZoomPresentation();

  return {
    updateHouse(house: ReviewHouse) {
      houseById.set(house.id, house);
      const marker = markerById.get(house.id);
      if (!marker) return;
      const zoom = mapZoom(map);
      marker.setStyle(markerOptions(house, renderer, selectedIds.has(house.id), zoom));
      const shouldShow = housePassesFilter(house) && (mapZoomBand(zoom) !== 'FAR' || selectedIds.has(house.id));
      if (shouldShow && !map.hasLayer(marker)) marker.addTo(map);
      if (!shouldShow && map.hasLayer(marker)) map.removeLayer(marker);
      marker.setPopupContent(popupHtml(house));
    },
    updateHouses(houses: ReviewHouse[]) {
      for (const house of houses) this.updateHouse(house);
      renderSelection();
    },
    clearSelection,
    setChangesOnly(enabled: boolean) {
      setFilter(enabled ? 'CHANGES' : 'ALL');
    },
    setFilter,
    destroy() {
      window.removeEventListener('keydown', handleKeydown);
      modeTools?.removeEventListener('click', handleModeControl);
      modeTools?.removeEventListener('pointerup', handleModeControl);
      element.removeEventListener('pointerdown', beginPointerGesture, true);
      element.removeEventListener('pointermove', movePointerGesture, true);
      element.removeEventListener('pointerup', finishPointerGesture, true);
      element.removeEventListener('pointercancel', cancelPointerGestures, true);
      element.removeEventListener('contextmenu', suppressContextMenu);
      map.off('click', showNearestPopup);
      map.off('zoomend', refreshZoomPresentation);
      cancelPointerGestures();
      pageFriendlyMap.destroy();
      map.remove();
    }
  };
}

function markerOptions(
  house: ReviewHouse,
  renderer: any,
  selected: boolean,
  zoom: number
): Record<string, unknown> {
  const status = house.inclusionStatus;
  const change = house.reviewRequired === true;
  const band = mapZoomBand(zoom);
  const fillColor = selected
    ? SELECTED_COLOR
    : change
      ? CHANGE_COLOR
      : status === 'INCLUDED'
        ? INCLUDED_COLOR
        : EXCLUDED_COLOR;
  const radius = selected
    ? zoomInterpolate(zoom, 4, 6.5)
    : change
      ? zoomInterpolate(zoom, 1.5, 5.25)
      : status === 'INCLUDED'
        ? zoomInterpolate(zoom, 0.5, 4.2)
        : zoomInterpolate(zoom, 0.75, 4.7);
  const weight = selected
    ? zoomInterpolate(zoom, 2, 2.8)
    : change
      ? zoomInterpolate(zoom, 0.4, 2)
      : zoomInterpolate(zoom, 0, 1.5);
  return {
    renderer,
    radius,
    weight,
    color: '#ffffff',
    fillColor,
    fillOpacity: band === 'FAR'
      ? (selected ? 0.98 : 0)
      : band === 'MEDIUM'
        ? (selected ? 0.98 : change ? 0.9 : status === 'INCLUDED' ? 0.48 : 0.58)
        : (selected ? 0.98 : change ? 0.98 : status === 'INCLUDED' ? 0.9 : 0.78)
  };
}

function reviewStreetStyle(zoom: number, renderer: any): Record<string, unknown> {
  const band = mapZoomBand(zoom);
  return {
    color: STREET_COLOR,
    weight: zoomInterpolate(zoom, 1, 2.8),
    opacity: band === 'FAR' ? 0.12 : band === 'MEDIUM' ? 0.32 : 0.55,
    renderer
  };
}

function isUnmatchedIncluded(house: ReviewHouse): boolean {
  return house.inclusionStatus === 'INCLUDED' && (house.matchStatus === 'UNMATCHED' || !house.unitId);
}

function normalizeStreetName(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function streetCore(value: string): string {
  return normalizeStreetName(value)
    .replace(/\b(NORTH|SOUTH|EAST|WEST|N|S|E|W)\b/g, ' ')
    .replace(/\b(STREET|ST|AVENUE|AVE|ROAD|RD|DRIVE|DR|LANE|LN|COURT|CT|PLACE|PL|BOULEVARD|BLVD|TRAIL|TRL|WAY|PARKWAY|PKWY|CIRCLE|CIR)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function projectDistanceMeters(point: { lat: number; lng: number }, coordinates: Array<[number, number]>): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < coordinates.length; index += 1) {
    const start = coordinates[index - 1]!;
    const end = coordinates[index]!;
    const lat0 = point.lat * Math.PI / 180;
    const metersPerLng = 111320 * Math.cos(lat0);
    const metersPerLat = 110540;
    const px = (point.lng - start[0]) * metersPerLng;
    const py = (point.lat - start[1]) * metersPerLat;
    const bx = (end[0] - start[0]) * metersPerLng;
    const by = (end[1] - start[1]) * metersPerLat;
    const lengthSquared = bx * bx + by * by;
    const ratio = lengthSquared ? Math.max(0, Math.min(1, (px * bx + py * by) / lengthSquared)) : 0;
    minimum = Math.min(minimum, Math.hypot(px - ratio * bx, py - ratio * by));
  }
  return minimum;
}

function formatDistance(meters: number): string {
  if (meters < 30) return `${Math.max(1, Math.round(meters * 3.28084))} ft`;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1609.344).toFixed(1)} mi`;
}

function tapTolerancePixels(pointerType: string | undefined, element: HTMLElement): number {
  const coarsePointer = pointerType === 'touch'
    || window.matchMedia?.('(pointer: coarse)').matches === true
    || element.clientWidth <= 760;
  return coarsePointer
    ? COARSE_POINTER_TAP_TOLERANCE_PIXELS
    : FINE_POINTER_TAP_TOLERANCE_PIXELS;
}

function nearestHouseId(
  point: ScreenPoint,
  map: any,
  houseById: Map<string, ReviewHouse>,
  maximumDistance: number
): string | null {
  let nearestId: string | null = null;
  let nearestDistance = maximumDistance;
  for (const [houseId, house] of houseById) {
    const housePoint = map.latLngToContainerPoint([house.lat, house.lng]);
    const distance = Math.hypot(housePoint.x - point.x, housePoint.y - point.y);
    if (distance <= nearestDistance) {
      nearestId = houseId;
      nearestDistance = distance;
    }
  }
  return nearestId;
}

function isSecondaryPointer(event: PointerEvent): boolean {
  return event.button === 2 || (event.button === 0 && event.ctrlKey && !event.metaKey);
}

function isClosedLasso(points: ScreenPoint[]): boolean {
  if (points.length < 4) return false;
  const pathLength = polylineLength(points);
  if (pathLength < LASSO_MIN_LENGTH_PIXELS) return false;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const closeDistance = Math.hypot(last.x - first.x, last.y - first.y);
  return closeDistance <= LASSO_CLOSE_TOLERANCE_PIXELS && Math.abs(polygonArea(points)) >= LASSO_MIN_AREA_PIXELS;
}

function smoothSvgPath(points: ScreenPoint[], close: boolean): string {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0]!.x} ${points[0]!.y}`;

  let path = `M ${points[0]!.x.toFixed(1)} ${points[0]!.y.toFixed(1)}`;
  for (let index = 1; index < points.length - 1; index += 1) {
    const current = points[index]!;
    const next = points[index + 1]!;
    const midpointX = (current.x + next.x) / 2;
    const midpointY = (current.y + next.y) / 2;
    path += ` Q ${current.x.toFixed(1)} ${current.y.toFixed(1)} ${midpointX.toFixed(1)} ${midpointY.toFixed(1)}`;
  }
  const last = points[points.length - 1]!;
  path += ` L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
  if (close) path += ' Z';
  return path;
}

function pointInPolygon(point: ScreenPoint, polygon: ScreenPoint[]): boolean {
  let inside = false;
  for (let current = 0, previous = polygon.length - 1; current < polygon.length; previous = current, current += 1) {
    const currentPoint = polygon[current]!;
    const previousPoint = polygon[previous]!;
    const crosses = (currentPoint.y > point.y) !== (previousPoint.y > point.y)
      && point.x < ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y))
        / ((previousPoint.y - currentPoint.y) || Number.EPSILON) + currentPoint.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function distanceToPolyline(point: ScreenPoint, polyline: ScreenPoint[], closed: boolean): number {
  if (!polyline.length) return Number.POSITIVE_INFINITY;
  if (polyline.length === 1) return Math.hypot(point.x - polyline[0]!.x, point.y - polyline[0]!.y);

  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < polyline.length; index += 1) {
    minimum = Math.min(minimum, distanceToSegment(point, polyline[index - 1]!, polyline[index]!));
  }
  if (closed) minimum = Math.min(minimum, distanceToSegment(point, polyline[polyline.length - 1]!, polyline[0]!));
  return minimum;
}

function distanceToSegment(point: ScreenPoint, start: ScreenPoint, end: ScreenPoint): number {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;
  if (!lengthSquared) return Math.hypot(point.x - start.x, point.y - start.y);
  const projection = Math.max(0, Math.min(1,
    ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) / lengthSquared
  ));
  const nearestX = start.x + projection * deltaX;
  const nearestY = start.y + projection * deltaY;
  return Math.hypot(point.x - nearestX, point.y - nearestY);
}

function polylineLength(points: ScreenPoint[]): number {
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    length += Math.hypot(points[index]!.x - points[index - 1]!.x, points[index]!.y - points[index - 1]!.y);
  }
  return length;
}

function polygonArea(points: ScreenPoint[]): number {
  let area = 0;
  for (let current = 0, previous = points.length - 1; current < points.length; previous = current, current += 1) {
    area += points[previous]!.x * points[current]!.y - points[current]!.x * points[previous]!.y;
  }
  return area / 2;
}

function isLeafletControl(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('.leaflet-control'));
}

function popupHtml(house: ReviewHouse): string {
  const state = house.inclusionStatus === 'INCLUDED' ? 'Included as a house' : 'Excluded from sections';
  const match = house.matchStatus === 'MATCHED' ? '' : `<br><em>Street match: ${escapeHtml(house.matchStatus.toLowerCase())}</em>`;
  const change = house.reviewRequired && house.recheckStatus
    ? `<br><strong class="review-popup-change">Recheck: ${escapeHtml(recheckLabel(house.recheckStatus))}</strong>`
    : '';
  const condition = house.conditionStatus
    ? `<br><small>${escapeHtml(conditionLabel(house.conditionStatus))}</small>`
    : '';
  const screening = screeningPopupHtml(house);
  return `<strong>${escapeHtml(house.address || 'Candidate house')}</strong><br>${escapeHtml(house.street)}${match}${change}${condition}${screening}<br><small>${state}</small>`;
}

function screeningPopupHtml(house: ReviewHouse): string {
  if (!house.screeningStatus) return '';
  const confidence = house.screeningConfidence ? ` · ${house.screeningConfidence.toLocaleLowerCase()} confidence` : '';
  const evidence = [
    house.sourceAddressType ? `Address type: ${house.sourceAddressType}` : '',
    house.sourceUnitType || house.sourceUnitNumber ? `Unit: ${[house.sourceUnitType, house.sourceUnitNumber].filter(Boolean).join(' ')}` : '',
    house.zoningCode ? `Zoning: ${house.zoningCode}` : '',
    house.parcelYearBuilt ? `Year built: ${house.parcelYearBuilt}` : '',
    house.businessName ? `Business match: ${house.businessName}` : house.businessMatch ? 'Business-location match' : '',
    (house.apnAddressCount ?? 0) > 1 ? `${house.apnAddressCount} addresses share the parcel` : ''
  ].filter(Boolean).slice(0, 4);
  const reasons = (house.screeningReasons ?? []).slice(0, 3);
  const override = house.screeningOverride
    ? `<br><small>Admin decision: ${house.screeningOverride === 'INCLUDE' ? 'keep as house' : 'exclude'}</small>`
    : '';
  return `<br><strong>${escapeHtml(screeningLabel(house.screeningStatus))}</strong><small>${escapeHtml(confidence)}</small>${[...evidence, ...reasons].map((item) => `<br><small>• ${escapeHtml(item)}</small>`).join('')}${override}`;
}

function screeningLabel(status: NonNullable<ReviewHouse['screeningStatus']>): string {
  if (status === 'RESIDENTIAL') return 'Residential house candidate';
  if (status === 'LIKELY_NON_RESIDENTIAL') return 'Likely nonresidential';
  if (status === 'POSSIBLE_VACANT') return 'Possible empty lot';
  if (status === 'POSSIBLE_CONSTRUCTION') return 'Possible house under construction';
  return 'Residential use uncertain';
}

function recheckLabel(status: NonNullable<ReviewHouse['recheckStatus']>): string {
  if (status === 'NEW') return 'New house';
  if (status === 'CHANGED') return 'House data changed';
  return 'Missing from newest source';
}

function conditionLabel(status: NonNullable<ReviewHouse['conditionStatus']>): string {
  if (status === 'EMPTY_LOT') return 'Reported empty lot';
  if (status === 'UNDER_CONSTRUCTION') return 'Reported under construction';
  return 'Reported No Soliciting';
}

function emptyHandle(): AdminReviewMapHandle {
  return {
    updateHouse() {},
    updateHouses() {},
    clearSelection() {},
    setChangesOnly() {},
    setFilter() {},
    destroy() {}
  };
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character] ?? character));
}
